// File: src/components/Dashboard/InventorySummary.jsx

import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, Boxes, PackageCheck, PackageSearch } from "lucide-react";
import api from "@/lib/axios";

const toNum = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const isObj = (value) =>
  value && typeof value === "object" && !Array.isArray(value);

const formatUnits = (value) =>
  new Intl.NumberFormat("en-GB", {
    maximumFractionDigits: 0,
  }).format(Math.round(toNum(value, 0)));

function normalizeInventory(input) {
  if (Array.isArray(input)) {
    const totalUnits = input.reduce((acc, item) => {
      const qty =
        item?.on_hand_qty != null
          ? Number(item.on_hand_qty)
          : item?.quantity != null
            ? Number(item.quantity)
            : 0;

      return acc + (Number.isFinite(qty) ? qty : 0);
    }, 0);

    return {
      rows: input,
      totalUnits,
      lineCount: input.length,
      stockLines: input.length,
    };
  }

  if (isObj(input)) {
    const rows = Array.isArray(input.rows) ? input.rows : [];
    return {
      rows,
      totalUnits: toNum(input.totalUnits ?? input.total_units ?? input.total_on_hand, 0),
      lineCount: toNum(input.lineCount ?? input.stockLines ?? input.line_count ?? rows.length, 0),
      stockLines: toNum(input.stockLines ?? input.lineCount ?? input.stock_lines ?? rows.length, 0),
    };
  }

  return {
    rows: [],
    totalUnits: 0,
    lineCount: 0,
    stockLines: 0,
  };
}

const MetricRow = ({ label, value, icon, tone = "slate", strong = false }) => {
  const tones = {
    slate: "text-slate-700",
    sky: "text-sky-700",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
  };

  return (
    <div className="flex items-center justify-between gap-4 border-b border-slate-100 py-4 last:border-b-0">
      <div className="flex min-w-0 items-center gap-3">
        <div className={`rounded-2xl bg-slate-100 p-2.5 ${tones[tone] || tones.slate}`}>
          {icon}
        </div>
        <span className="text-sm font-semibold text-slate-700">{label}</span>
      </div>

      <span
        className={`shrink-0 text-right ${
          strong ? "text-3xl font-black text-slate-950" : "text-lg font-bold text-slate-900"
        }`}
      >
        {value}
      </span>
    </div>
  );
};

const InventorySummary = ({
  onClick,
  data = null,
  loading = false,
  error = "",
  source = null,
  refreshKey = 0,
}) => {
  const [localData, setLocalData] = useState(null);
  const [localLoading, setLocalLoading] = useState(true);
  const [localError, setLocalError] = useState("");

  const hasParentData = useMemo(() => isObj(data) || Array.isArray(data), [data]);

  useEffect(() => {
    let cancelled = false;

    if (hasParentData) {
      setLocalData(normalizeInventory(data));
      setLocalLoading(false);
      setLocalError("");
      return () => {
        cancelled = true;
      };
    }

    setLocalLoading(true);
    setLocalError("");

    (async () => {
      try {
        const { data: responseData } = await api.get("/solo-mdsh-api/inventory/stock-levels");
        if (!cancelled) {
          setLocalData(normalizeInventory(responseData));
        }
      } catch (err) {
        console.error("Error fetching inventory summary:", err);
        if (!cancelled) {
          setLocalError("Inventory summary is temporarily unavailable.");
          setLocalData(normalizeInventory([]));
        }
      } finally {
        if (!cancelled) setLocalLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hasParentData, data, refreshKey]);

  const view = localData || normalizeInventory([]);
  const effectiveLoading = loading && !hasParentData ? true : localLoading;
  const effectiveError = error || localError;

  const headline = useMemo(() => {
    if (effectiveLoading) return "Loading live inventory position...";
    if (effectiveError) return effectiveError;
    if (view.lineCount === 0) return "No stock lines were returned.";
    return "Live stock position across current inventory lines.";
  }, [effectiveLoading, effectiveError, view.lineCount]);

  const clickable = typeof onClick === "function";

  if (effectiveLoading) {
    return (
      <section className="h-full rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-emerald-50 to-cyan-50 p-6 shadow-sm animate-pulse">
        <div className="h-6 w-40 rounded bg-slate-200" />
        <div className="mt-5 h-16 rounded-2xl bg-slate-100" />
        <div className="mt-5 h-40 rounded-2xl bg-slate-100" />
      </section>
    );
  }

  return (
    <section
      onClick={clickable ? onClick : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={`h-full rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-emerald-50 to-cyan-50 p-6 shadow-sm transition ${
        clickable ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-lg" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-emerald-700">
            Inventory control
          </div>
          <h3 className="mt-3 text-2xl font-black text-slate-950">Inventory</h3>
          <p className="mt-1 text-sm text-slate-600">
            Stock levels, line coverage, and live inventory visibility.
          </p>
        </div>

        {clickable ? (
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold text-sky-700">
            Open dashboard
            <ArrowRight size={14} />
          </div>
        ) : null}
      </div>

      {source ? <div className="mt-3 text-[11px] text-slate-500">Source: {source}</div> : null}

      <div
        className={`mt-5 rounded-2xl border px-4 py-3 text-sm font-semibold ${
          effectiveError
            ? "border-amber-200 bg-amber-50 text-amber-800"
            : "border-emerald-200 bg-emerald-50 text-emerald-900"
        }`}
      >
        {headline}
      </div>

      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
        <MetricRow
          label="Units On Hand"
          value={effectiveError ? "Unavailable" : formatUnits(view.totalUnits)}
          icon={<Boxes size={18} />}
          tone="emerald"
          strong
        />

        <MetricRow
          label="Stock Lines"
          value={effectiveError ? "—" : formatUnits(view.lineCount)}
          icon={<PackageCheck size={18} />}
          tone="sky"
        />

        <MetricRow
          label="Coverage Rows"
          value={effectiveError ? "—" : formatUnits(view.rows.length)}
          icon={<PackageSearch size={18} />}
          tone="amber"
        />
      </div>

      <div className="mt-5 rounded-2xl border border-slate-200 bg-white/80 p-4 text-sm text-slate-600">
        Total units are calculated from{" "}
        <span className="font-bold text-slate-800">on_hand_qty</span>, with a fallback to{" "}
        <span className="font-bold text-slate-800">quantity</span>.
      </div>
    </section>
  );
};

export default InventorySummary;